import React, { ReactNode, useCallback, useMemo } from 'react';
import { BaseFormItem } from '../base-form-item/BaseFormItem';
import { FieldRenderProps, Validator } from '../../form';
import { TextInput, ValidatingTextInput } from '../../input';

/**
 * {@docCategory TextInputFormItem}
 */
export interface TextInputFormItemProps {
   /**
    * The name of the input item.
    */
   name: string;

   /**
    * Whether or not the label should be rendered inline
    */
   inline?: boolean;

   /**
    * The label for the input item.
    */
   label?: string;

   /**
    * Set this to disable word wrap on the label text
    */
   disableLabelWordWrap?: boolean;

   /**
    * Allow the contents to stretch to its parent width
    */
   horizontalFill?: boolean;

   /**
    * The hint for the input item.
    */
   hint?: string;

   /**
    * The help text (info icon tooltip) for the input item.  Setting this value
    * will cause an info icon to be displayed that has a tooltip containing
    * this text.
    */
   helpText?: string;

   /**
    * The placeholder text for the input item.
    */
   placeholder?: string;

   /**
    * The inputMode for the input item.
    * @defaultvalue 'text'
    */
   inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';

   /**
    * Hint for how to handle automatic capitalization.
    * @defaultvalue sentences
    */
   autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';

   /**
    * Hint for how to handle automatic completion.
    * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
    */
   autoComplete?: string | 'off' | 'on';

   /**
    * Sets the input item to read-only.
    * @defaultvalue false
    */
   readOnly?: boolean;

   /**
    * Set the input item to disabled.
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * Enable the clear feature.
    * @defaultvalue false
    */
   allowClear?: boolean;

   /**
    * The prefix element.
    */
   prefix?: ReactNode;

   /**
    * The suffix element.
    */
   suffix?: ReactNode;

   /**
    * The label text displayed before (on the left side of) the input field.
    */
   addonBefore?: ReactNode;

   /**
    * The label text displayed after (on the right side of) the input field.
    */
   addonAfter?: ReactNode;

   /**
    * Set the input item to be required.
    * @defaultvalue false
    */
   required?: boolean;

   /**
    * The maximum width that the label can occupy.  For inline labels, this is the total width
    * allocated to the label.
    */
   maxLabelWidth?: number | string;

   /**
    * Set the minimum length of the text.
    */
   minLength?: number;

   /**
    * Set the maximum length of the text.
    */
   maxLength?: number;

   /**
    * Function to call to validate the data.
    */
   validator?: Validator;

   /**
    * Sets the width.
    */
   width?: string | number;

   /**
    * Sets the min width.
    */
   minWidth?: string | number;

   /**
    * Sets the max width.
    */
   maxWidth?: string | number;

   /**
    * Set this to focus on form item on first render
    */
   autoFocus?: boolean;

   /**
    * The pattern specifies a regular expression that the <input> element's value is checked against
    */
   inputKeyFilter?: string | RegExp | ((value: string) => boolean);

   /**
    * Whether or not to trigger change/validation events on blur or with every change.
    * @defaultvalue true
    */
   triggerOnBlur?: boolean;

   /**
    * Callback called when the value is changed.
    * @param value - The value
    */
   onChange?: (value: string | undefined) => void;

   /**
    * Function called when the user presses the enter key.
    */
   onPressEnter?: React.KeyboardEventHandler<HTMLInputElement>;
}

export const TextInputFormItem = (props: TextInputFormItemProps) => {

   return (
      <BaseFormItem<string>
         name={props.name}
         inline={props.inline}
         label={props.label}
         disableLabelWordWrap={props.disableLabelWordWrap}
         horizontalFill={props.horizontalFill}
         hint={props.hint}
         helpText={props.helpText}
         required={props.required}
         maxLabelWidth={props.maxLabelWidth}
         minLength={props.minLength}
         maxLength={props.maxLength}
         validator={props.validator}
         render={fieldRenderer(props)}
         triggerOnBlur={props.triggerOnBlur}
      />
   );
};

const fieldRenderer = (props: TextInputFormItemProps) => {

   return (field: FieldRenderProps<string | undefined>) => {

      const onChange = props.onChange;
      const fieldInputOnChange = field.input.onChange;

      const onChangeHandler = useCallback((value?: string) => {
         fieldInputOnChange(value);
         if (onChange) {
            onChange(value);
         }
      }, [onChange, fieldInputOnChange]);

      const { name, placeholder, readOnly, disabled, allowClear, prefix, suffix, addonBefore, addonAfter, maxLength, width: widthProp, minWidth, maxWidth, autoFocus, onPressEnter, horizontalFill, triggerOnBlur=true, inputKeyFilter } = props;
      const { fieldErrorClassName, input } = field;
      const { onBlur, onFocus, value } = input;

      const width = widthProp ? widthProp : (horizontalFill ? '100%' : undefined);

      return (
         useMemo(() => {
            return (triggerOnBlur ?
               <ValidatingTextInput
                  name={name}
                  className={fieldErrorClassName}
                  placeholder={placeholder}
                  inputMode={props.inputMode}
                  autoCapitalize={props.autoCapitalize}
                  autoComplete={props.autoComplete}
                  readOnly={readOnly}
                  disabled={disabled}
                  allowClear={allowClear}
                  prefix={prefix}
                  suffix={suffix}
                  addonBefore={addonBefore}
                  addonAfter={addonAfter}
                  maxLength={maxLength}
                  value={value}
                  width={width}
                  minWidth={minWidth}
                  maxWidth={maxWidth}
                  autoFocus={autoFocus}
                  onChange={onChangeHandler}
                  onBlur={onBlur}
                  onFocus={onFocus}
                  onPressEnter={onPressEnter}
                  inputKeyFilter={inputKeyFilter}
               /> :
               <TextInput
                 name={name}
                 className={fieldErrorClassName}
                 placeholder={placeholder}
                 inputMode={props.inputMode}
                 autoCapitalize={props.autoCapitalize}
                 autoComplete={props.autoComplete}
                 readOnly={readOnly}
                 disabled={disabled}
                 allowClear={allowClear}
                 prefix={prefix}
                 suffix={suffix}
                 addonBefore={addonBefore}
                 addonAfter={addonAfter}
                 maxLength={maxLength}
                 value={value}
                 width={width}
                 minWidth={minWidth}
                 maxWidth={maxWidth}
                 autoFocus={autoFocus}
                 onChange={onChangeHandler}
                 onBlur={onBlur}
                 onFocus={onFocus}
                 onPressEnter={onPressEnter}
                 triggerOnBlur={false}
                 inputKeyFilter={inputKeyFilter}
               />
            );
         },[name, placeholder, readOnly, disabled, allowClear, prefix, suffix, addonBefore, addonAfter, maxLength, width, minWidth, maxWidth, autoFocus, onPressEnter, fieldErrorClassName, value, onBlur, onFocus, onChangeHandler, triggerOnBlur, inputKeyFilter ])
      );
   };
};
